import { useTranslation } from 'react-i18next';
import { useCallback } from 'react';
import { color as token, typography } from '@figma/fpl-tokens/tokens.stylex';
import { FlexCenVer } from '@/UI/styles';
import { TextLgRg } from '@/UI/AppTypography/AppTypography';
import { useMediaQuery, useTheme } from '@/UI/theme.exports';
import useWorkspacesStore from '@/state/workspaces.state';
import { PlusIcon } from '@/UI/Icons';
import { ButtonContained } from '@/UI/Buttons/AppButton';
import { TrackTypeEnum, useAnalytics } from '@/hooks/useAnalytics';
import { useToolTabsEnabled } from '@/hooks/useToolTabsEnabled';
import { I18N_KEYS } from '../../language/keys';

interface DashboardHeaderProps {
  isShowCreateRecipeButton: boolean;
  createRecipe: () => Promise<void>;
}

export const DashboardHeader = ({ isShowCreateRecipeButton, createRecipe }: DashboardHeaderProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isNarrowScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { track } = useAnalytics();
  const areTabsEnabled = useToolTabsEnabled();
  const workspaceName = useWorkspacesStore((state) => state.activeWorkspace.workspaceName);
  const createLabel = t(
    areTabsEnabled ? I18N_KEYS.MAIN_DASHBOARD.CREATE_NEW_WORKFLOW : I18N_KEYS.MAIN_DASHBOARD.CREATE_NEW_FILE,
  );
  const createLabelShort = t(
    areTabsEnabled
      ? I18N_KEYS.MAIN_DASHBOARD.CONTEXT_MENU.CREATE_WORKFLOW
      : I18N_KEYS.MAIN_DASHBOARD.CONTEXT_MENU.CREATE_FILE,
  );

  const onCreateRecipe = useCallback(() => {
    track('created_new_flow', { source: 'dashboard_header' }, TrackTypeEnum.BI);
    void createRecipe();
  }, [createRecipe, track]);

  return (
    <FlexCenVer
      sx={{
        minHeight: '40px',
        justifyContent: 'space-between',
        gap: 1,
        flexShrink: 0,
        minWidth: 0,
      }}
    >
      <TextLgRg
        color={token.text}
        noWrap
        title={workspaceName}
        sx={{ minWidth: 0, flex: 1, fontSize: typography.textBodyLargeFontSize }}
      >
        {workspaceName}
      </TextLgRg>
      {isShowCreateRecipeButton && (
        <ButtonContained
          aria-label={createLabel}
          onClick={onCreateRecipe}
          startIcon={<PlusIcon width={16} height={16} />}
          sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
        >
          {isNarrowScreen ? createLabelShort : createLabel}
        </ButtonContained>
      )}
    </FlexCenVer>
  );
};
